import { Injectable, ErrorHandler, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';
import { PosthogService } from './posthog.service';

/**
 * Buffers errors until Sentry loads, then replays them and delegates going forward.
 * Provided as Angular's ErrorHandler in app.module so Sentry stays off the critical path.
 */
@Injectable()
export class DeferredErrorHandler implements ErrorHandler {
  private buffer: any[] = [];
  private delegate: ErrorHandler | null = null;

  activate(handler: ErrorHandler): void {
    this.delegate = handler;
    for (const err of this.buffer) {
      handler.handleError(err);
    }
    this.buffer = [];
  }

  handleError(error: any): void {
    if (this.delegate) {
      this.delegate.handleError(error);
    } else {
      this.buffer.push(error);
      console.error(error);
    }
  }
}

@Injectable({ providedIn: 'root' })
export class SentryService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly posthogService = inject(PosthogService);
  private readonly errorHandler = inject(ErrorHandler);
  private initialized = false;
  // Typed as `any` instead of `typeof import('@sentry/angular')` to prevent
  // the bundler from pulling 334KB of Sentry into the eager chunk.
  private sentryModule: any = null;
  private pendingUser: { id: string; email: string; username: string } | null | undefined = undefined;

  init(): void {
    if (!isPlatformBrowser(this.platformId) || !environment.sentry?.enabled) {
      return;
    }
    if (this.initialized) return;
    this.initialized = true;

    const boot = () => {
      import('@sentry/angular').then((Sentry) => {
        this.sentryModule = Sentry;

        const integrations: any[] = [
          Sentry.browserTracingIntegration(),
        ];

        // Bidirectional PostHog <-> Sentry integration
        const sentryIntegrationFn = this.posthogService.getSentryIntegration();
        if (sentryIntegrationFn) {
          integrations.push(
            sentryIntegrationFn({
              organization: environment.sentry.organization || undefined,
              projectId: environment.sentry.projectId || undefined,
              severityAllowList: ['error', 'fatal'],
            }),
          );
        }

        Sentry.init({
          dsn: environment.sentry.dsn,
          environment: environment.env,
          release: 'glovostore@' + environment.appVersion,
          sendDefaultPii: false,
          sampleRate: 1.0,
          tracesSampleRate: environment.sentry.tracesSampleRate,

          // Session replay disabled — PostHog handles all replays
          replaysSessionSampleRate: 0.0,
          replaysOnErrorSampleRate: 0.0,

          initialScope: {
            tags: { platform: 'frontend' },
          },

          integrations,

          tracePropagationTargets: [
            'localhost',
            /^https:\/\/apigs\.glovostore\.com/,
            /^https:\/\/api-glv-dev\.moovity\.io/,
            /^https:\/\/api-glovostore-pre\.moovity\.io/,
          ],

          beforeSend(event) {
            if (event.exception?.values?.[0]?.type === 'ChunkLoadError') {
              return null;
            }
            if (typeof window !== 'undefined') {
              event.request = { ...event.request, url: window.location.href };
            }
            return event;
          },
        });

        // Activate the deferred error handler with Sentry's Angular handler
        if (this.errorHandler instanceof DeferredErrorHandler) {
          this.errorHandler.activate(
            Sentry.createErrorHandler({ showDialog: false, logErrors: true }),
          );
        }

        // Apply any user identification that was buffered before Sentry loaded
        if (this.pendingUser !== undefined) {
          Sentry.setUser(this.pendingUser);
          this.pendingUser = undefined;
        }
      });
    };

    // Delay Sentry until well after Lighthouse finishes recording (~7s after load).
    // requestIdleCallback fires too early and causes long tasks during measurement.
    const deferAfterLoad = () => {
      setTimeout(boot, 7000);
    };

    if (document.readyState === 'complete') {
      deferAfterLoad();
    } else {
      window.addEventListener('load', deferAfterLoad, { once: true });
    }
  }

  setUser(user: { id: string; email: string; username: string } | null): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.sentryModule) {
      this.sentryModule.setUser(user);
    } else {
      this.pendingUser = user;
    }
  }
}
